'use client';

import Popup from '@/components/Popup';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { formatNumber, setSession, getIntlLocale } from '@/utils/tools';
import { CarFilled, InfoOutlined, RightArrowOutlined, ShoppingBagFilled } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useCartSensorsTracking } from '../hooks/useCartSensorsTracking';
import Image from '@/components/Image';
import QuestionResponsive from '@/components/IconResponsive/QuestionResponsive';

interface FreeShipData {
  surplus?: number;
  copywriting?: string;
  symbol_prefix?: string;
  symbol_suffix?: string;
  pop_status?: number;
}

interface HalfPriceRule {
  rule_flag?: number;
  discount_rate?: string;
  threshold_price?: number;
  restrict_single?: number;
}

interface PublicHalfPrice {
  status?: number;
  title?: string;
  content?: string;
  rule?: HalfPriceRule;
}

interface PublicFreeShipping {
  status?: number;
  title?: string;
  content?: string;
  rule?: {
    ordinary_free_threshold?: number;
    ordinary_free_country?: string;
  };
}

interface NoticeBarProps {
  freeShipData: any;
  recommendList: object;
  totalData: any;
  batchFlag: boolean;
  deviceType: EPlatform;
}

interface CartFSA {
  status?: number;
  title?: string;
  pc_tips_images?: string;
  content_key?: number;
}
const NoticeBar: React.FC<NoticeBarProps> = ({
  freeShipData: initialFreeShipData,
  recommendList,
  totalData,
  batchFlag,
  deviceType,
}) => {
  const t = useTranslations('cart.notice');
  const [showSalePop, setShowSalePop] = useState(false);
  const [show50Off, setShow50Off] = useState(false);
  const [freeTripText, setFreeTripText] = useState('');
  const [freeShipData, setFreeShipData] = useState<FreeShipData>(initialFreeShipData || {});

  const router = useRouter();
  const currencyList = useStore((state) => state.currencyList);
  const locale = getIntlLocale(env.NEXT_PUBLIC_SITE_NAME);

  // 直接使用埋点hook
  const { trackCartFunctionBtnClick } = useCartSensorsTracking();
  const setDynamicResourceDialog = useStore((state) => state.setDynamicResourceDialog);
  // 直接从 publicResourceData 获取配置，避免单独的 state
  const publicResourceData = useStore((state) => state.publicResourceData);
  const publicFreeShipping: PublicFreeShipping =
    (publicResourceData?.config_names as any)?.product_content_free_shipping_member || {};
  const publicHalfPrice: PublicHalfPrice =
    (publicResourceData?.config_names as any)?.product_content_half_price_member || {};
  const cartFSA: CartFSA = (publicResourceData?.jsonvalues as any)?.cart_FSA || {};
  // 获取当前币种
  const currency = currencyList.find((item) => item.name === env.NEXT_PUBLIC_DEFAULT_CURRENCY);

  // 处理"Select Others"按钮点击事件
  const handleSelectOthersClick = () => {
    // 🔍 直接调用神策埋点 - 购物车功能按钮点击
    trackCartFunctionBtnClick('Select Others');
    // 添加路由跳转逻辑
    const cart_rec_frame_group_id = (recommendList as any)?.cart_rec_frame_group_id || '';
    if (cart_rec_frame_group_id) {
      setSession('zl_goodsListEnterPageScene', '购物车页公告条');
      router.push(`/goods-list/${cart_rec_frame_group_id}`);
    }
  };

  // 获取包邮政策内容（用于弹窗显示，对应Vue的publicFreeShippingContent）
  const getPublicFreeShippingContent = () => {
    if (publicFreeShipping.status === 1) {
      let content = publicFreeShipping.content || '';
      if (
        content.includes('{threshold_price}') &&
        publicFreeShipping.rule?.ordinary_free_threshold
      ) {
        content = content.replace(
          '{threshold_price}',
          `<span class="text-primary-purple">${formatPriceNumberWithSymbol(String(publicFreeShipping.rule.ordinary_free_threshold), currency)}</span>`,
        );
      }
      if (
        content.includes('{ordinary_free_country}') &&
        publicFreeShipping.rule?.ordinary_free_country
      ) {
        content = content.replace(
          '{ordinary_free_country}',
          `<span class="text-primary-purple">${publicFreeShipping.rule.ordinary_free_country}</span>`,
        );
      }
      if (
        content.includes('{ordinary_free_threshold}') &&
        publicFreeShipping.rule?.ordinary_free_threshold
      ) {
        content = content.replace(
          '{ordinary_free_threshold}',
          `<span class="text-primary-purple">${String(publicFreeShipping.rule.ordinary_free_threshold)}</span>`,
        );
      }
      // 将\n换行符转换为<br>标签
      content = content.replace(/\\n/g, '<br>').replace(/\n/g, '<br>');
      return content;
    }
    return '';
  };

  // 获取第二件半价标题（对应Vue的productContentHalfPriceTitle）
  const getProductContentHalfPriceTitle = () => {
    if (publicHalfPrice?.rule?.rule_flag === 1) {
      let title = publicHalfPrice.title || '';
      if (title.includes('{discount_rate}') && publicHalfPrice.rule.discount_rate) {
        title = title.replace('{discount_rate}', publicHalfPrice.rule.discount_rate);
      }
      return title;
    }
    return publicHalfPrice?.title || '';
  };

  // 获取第二件半价内容（用于弹窗显示，对应Vue的publicHalfContent）
  const getPublicHalfContent = () => {
    if (publicHalfPrice?.rule?.rule_flag === 1) {
      let content = publicHalfPrice.content || '';
      if (content.includes('{discount_rate}') && publicHalfPrice.rule.discount_rate) {
        content = content.replace(
          '{discount_rate}',
          `<span class="text-primary-purple">${publicHalfPrice.rule.discount_rate}</span>`,
        );
      }
      if (content.includes('{threshold_price}') && publicHalfPrice.rule.threshold_price) {
        content = content.replace(
          '{threshold_price}',
          `<span class="text-primary-purple">${formatPriceNumberWithSymbol(String(publicHalfPrice.rule.threshold_price), currency)}</span>`,
        );
      }
      if (content.includes('{restrict_single}') && publicHalfPrice.rule.restrict_single) {
        content = content.replace(
          '{restrict_single}',
          `<span class="text-primary-purple">${String(publicHalfPrice.rule.restrict_single)}</span>`,
        );
      }
      // 将\n换行符转换为<br>标签
      content = content.replace(/\\n/g, '<br>').replace(/\n/g, '<br>');
      return content;
    }
    return '';
  };

  // 获取第二件半价信息（对应Vue的getHalfPrice方法）
  const getHalfPriceInfo = async () => {
    try {
      setShow50Off(publicHalfPrice?.rule?.rule_flag === 1);
    } catch (error) {
      logger.log('error', error);
      // 出错时仍然设置show50Off
      setShow50Off(publicHalfPrice?.rule?.rule_flag === 1);
    }
  };

  // 监听initialFreeShipData变化，更新本地状态
  useEffect(() => {
    if (initialFreeShipData) {
      setFreeShipData(initialFreeShipData);
    }
  }, [initialFreeShipData]);

  // 监听freeShipData变化，更新freeTripText（对应Vue的freeTripText计算属性）
  useEffect(() => {
    const { surplus, copywriting, symbol_prefix, symbol_suffix } = freeShipData;

    if (surplus && copywriting) {
      // 有surplus时，显示动态文案（Add $X more...）
      if (deviceType === EPlatform.WAP) {
        // WAP端处理
        let newStr = copywriting
          .replace(
            copywriting.substring(copywriting.indexOf('free')),
            `<span>${copywriting.substring(copywriting.indexOf('free'))}</span>`,
          )
          .replace('{', '<span class="text-primary-purple font-bb">')
          .replace('}', '</span>')
          .replace('amount', `${symbol_prefix}${formatNumber(surplus, 2, locale)}${symbol_suffix}`);
        // 将\n换行符转换为<br>标签
        newStr = newStr.replace(/\\n/g, '<br>').replace(/\n/g, '<br>');
        setFreeTripText(newStr);
      } else {
        // PC端处理
        let newStr = copywriting.replace(
          /{amount}/,
          `${symbol_prefix}${formatNumber(surplus, 2, locale)}${symbol_suffix}`,
        );
        // 将\n换行符转换为<br>标签
        newStr = newStr.replace(/\\n/g, '<br>').replace(/\n/g, '<br>');
        setFreeTripText(newStr);
      }
    } else {
      // 没有surplus时，显示copywriting或空字符串（与Vue版本保持一致）
      let content = copywriting || '';
      // 将\n换行符转换为<br>标签
      content = content.replace(/\\n/g, '<br>').replace(/\n/g, '<br>');
      setFreeTripText(content);
    }
  }, [freeShipData, deviceType]);

  // 组件挂载时获取第二件半价信息
  useEffect(() => {
    getHalfPriceInfo();
  }, []);
  // 点击公告栏打开弹窗
  const handleOpenPop = () => {
    if (batchFlag) return; // 编辑模式下不弹出包邮政策弹窗

    const freShipFlag =
      freeShipData.pop_status === 1 &&
      publicFreeShipping?.status === 1 &&
      publicFreeShipping.content;
    const show50OffFlag =
      show50Off && publicHalfPrice?.rule?.rule_flag === 1 && publicHalfPrice.content;

    if (freShipFlag || show50OffFlag) {
      setShowSalePop(true);
    }
  };

  const handleClose = () => {
    setShowSalePop(false);
  };

  // 如果包邮和半价都没有开启，则不显示
  const hasContent =
    (publicFreeShipping.status === 1 && freeTripText) || publicHalfPrice.rule?.rule_flag === 1;

  if (!hasContent) {
    return null;
  }

  return (
    <>
      {/* PC端渲染 - 参照Vue版本的横向布局 */}
      <WebOnly>
        <div className="mb-[24px] min-h-[48px] bg-[#ecebfd] px-[16px] py-[14px]">
          <div className="flex items-center">
            {/* 第二件半价 section */}
            {publicHalfPrice.rule?.rule_flag === 1 && (
              <div className="flex items-center mr-[20px] last:mr-0">
                <ShoppingBagFilled className="text-[20px] text-primary-black" />
                <span className="ml-[8px] text-[#282828] font-bs text-[12px] 2xl:text-[14px]">
                  {getProductContentHalfPriceTitle()}
                </span>
                {totalData.discount === '0.00' && (
                  <div
                    className="flex items-center cursor-pointer text-[#282828] ml-[8px] text-[12px] 2xl:text-[14px]"
                    onClick={handleSelectOthersClick}
                  >
                    {t('selectOthers')}
                    <RightArrowOutlined className="text-[12px]" />
                  </div>
                )}
              </div>
            )}

            {/* 包邮政策 section */}
            {!!freeTripText && (
              <div className="flex items-center">
                <CarFilled className="text-[20px] text-primary-black" />
                <span
                  className="ml-[8px] text-[#282828] font-bs text-[12px] 2xl:text-[14px]"
                  dangerouslySetInnerHTML={{ __html: freeTripText }}
                />
              </div>
            )}
          </div>
          {cartFSA.status === 1 && (
            <div className="flex items-center mt-[8px]">
              <Image
                src={cartFSA?.pc_tips_images}
                alt="cartFSA"
                width={20}
                height={20}
                className="w-[20px] h-[20px]"
              />
              <span className="ml-[8px] text-[#282828] font-bs text-[12px] 2xl:text-[14px]">
                {cartFSA?.title}
              </span>
              <QuestionResponsive
                className="text-[16px] ml-[4px] cursor-pointer"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setDynamicResourceDialog({
                    show: true,
                    key: String(cartFSA?.content_key),
                  });
                }}
              />
            </div>
          )}
        </div>
      </WebOnly>

      {/* WAP端渲染 - 顶部通知栏 + 底部弹窗 */}
      <WapOnly>
        <div className="bg-[#ecebfd] py-[8px] px-[14px]">
          <div className="flex cursor-pointer" onClick={handleOpenPop}>
            <InfoOutlined className="text-[12px] mr-[6px] mt-[2px] flex-shrink-0" />
            <div className="text-[12px] flex-1">
              {freeTripText && (
                <p
                  className="leading-[12px] mb-[4px]"
                  dangerouslySetInnerHTML={{ __html: freeTripText }}
                />
              )}
              {show50Off && <p className="leading-[12px]">{getProductContentHalfPriceTitle()}</p>}
            </div>
          </div>
          {cartFSA.status === 1 && (
            <div
              className="flex items-center mt-[8px]"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setDynamicResourceDialog({
                  show: true,
                  key: String(cartFSA?.content_key),
                });
              }}
            >
              <QuestionResponsive className="text-[13px] mr-[6px]" />
              <span className="text-[12px] flex-1">{cartFSA?.title}</span>
            </div>
          )}
        </div>

        {/* 底部半屏弹窗 */}
        {showSalePop && (
          <Popup
            open={showSalePop}
            onClose={handleClose}
            position="bottom"
            height="50vh"
            title=""
            sx={{
              '& .popup-title': {
                borderBottom: 'none',
                padding: '10px',
              },
            }}
          >
            <div className="text-sm leading-relaxed">
              {freeShipData.pop_status === 1 &&
                publicFreeShipping.status === 1 &&
                publicFreeShipping.content && (
                  <div
                    className="mb-[26px] last:mb-0 leading-[18px] break-words"
                    dangerouslySetInnerHTML={{ __html: getPublicFreeShippingContent() }}
                  />
                )}
              {show50Off && publicHalfPrice.rule?.rule_flag === 1 && publicHalfPrice.content && (
                <div
                  className="mb-[26px] last:mb-0 leading-[18px] break-words"
                  dangerouslySetInnerHTML={{ __html: getPublicHalfContent() }}
                />
              )}
            </div>
          </Popup>
        )}
      </WapOnly>
    </>
  );
};

export default NoticeBar;
